import { LayoutConfirmation, pendingCalibrationMetrics } from '@/core/layout-calibration'
import type { CaptureProfile, RecognitionMetricKey } from '@/domain/types'
import type { GamePreset, LayoutDetection, LayoutSearchDiagnostic } from '@/presets/types'
import { performanceDiagnostics } from './performance-diagnostics'

const groups: readonly { name: 'hud' | 'map' | 'mesos'; metrics: readonly RecognitionMetricKey[]; budget: number }[] = [
  { name: 'hud', metrics: ['level', 'jobName', 'hp', 'mp', 'exp'], budget: 6_000 },
  { name: 'map', metrics: ['mapName'], budget: 6_000 },
  { name: 'mesos', metrics: ['mesos'], budget: 2_000 },
]
const retryDelays = [2_000, 5_000, 10_000, 30_000]
interface Search {
  epoch: number
  profile: CaptureProfile
  group: typeof groups[number]
  metrics: RecognitionMetricKey[]
  at: number
  expires: number
  controller: AbortController
  confirmation: LayoutConfirmation
  detection?: LayoutDetection
  misses: number
}

/** One disposable image/search, no frame queue and no additional capture clock. */
export class BackgroundLayoutSearch {
  private epoch = 0
  private task: Search | null = null
  private retries = new Map<string, { failures: number; after: number }>()
  private repairs = new Set<RecognitionMetricKey>()

  constructor(private readonly preset: GamePreset, private readonly interval: () => number) {}

  cancel(resetRetries = false): void {
    this.epoch++
    this.task?.controller.abort()
    this.task = null
    if (resetRetries) { this.retries.clear(); this.repairs.clear() }
  }

  needsFrame(profile: CaptureProfile): boolean {
    if (!this.preset.locateLayout || !this.preset.validateLayout || this.task) return false
    const pending = this.pendingMetrics(profile)
    return groups.some(group => group.metrics.some(metric => pending.includes(metric))
      && Date.now() >= (this.retries.get(group.name)?.after ?? 0))
  }

  start(frame: ImageData, profile: CaptureProfile, at: number): void {
    if (!this.needsFrame(profile)) return
    const pending = this.pendingMetrics(profile)
    const group = groups.find(group => group.metrics.some(metric => pending.includes(metric))
      && Date.now() >= (this.retries.get(group.name)?.after ?? 0))!
    const controller = new AbortController(), started = Date.now()
    const task: Search = { epoch: this.epoch, profile, group, controller, at, misses: 0,
      expires: started + group.budget + Math.max(10_000, 2 * this.interval()),
      metrics: group.metrics.filter(metric => pending.includes(metric)), confirmation: new LayoutConfirmation([...this.repairs]) }
    this.task = task
    const timer = setTimeout(() => controller.abort('timeout'), group.budget)
    const aborted = new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => {
      reject(new DOMException(String(controller.signal.reason ?? 'cancelled'), 'AbortError'))
    }, { once: true }))
    // Start after the current OCR has completed. Both paths use this same deadline.
    const search = Promise.resolve().then(() => this.preset.locateLayout!(frame, group.metrics, {
      signal: controller.signal, deadlineAt: started + group.budget,
      ...(group.name === 'mesos' ? { mesosBudgetMs: 1_000 } : {}),
      hint: { hud: profile.hudRect, regions: profile.autoRois ?? {} },
    }))
    void Promise.race([search, aborted]).then(detection => {
      if (!this.current(task)) return
      this.report(task, detection.diagnostics, Date.now() - started)
      if (!task.metrics.some(metric => detection.regions[metric])) { this.failed(task); return }
      task.detection = detection
      task.confirmation.observe(detection, task.metrics, at)
    }).catch(() => {
      if (!this.current(task, true)) return
      const timedOut = controller.signal.reason === 'timeout' || Date.now() >= started + group.budget
      this.report(task, { reason: timedOut ? 'timeout' : 'not-found', candidates: 0, durationMs: Date.now() - started }, Date.now() - started)
      this.failed(task)
    }).finally(() => clearTimeout(timer))
  }

  get awaitingConfirmation(): boolean { return Boolean(this.task?.detection) }

  /** Runtime rejected these saved anchors. Replace only after normal two-frame verification. */
  requestRepair(metrics: readonly RecognitionMetricKey[]): void {
    const hud = groups[0]!.metrics
    const brokenBars = metrics.filter(metric => metric === 'hp' || metric === 'mp' || metric === 'exp')
    for (const metric of brokenBars.length >= 2 ? hud : metrics) this.repairs.add(metric)
  }

  private pendingMetrics(profile: CaptureProfile): RecognitionMetricKey[] {
    const pending = new Set([...pendingCalibrationMetrics(profile), ...this.repairs])
    if (this.preset.id === 'gcw' && profile.roiOverrides.mesos) pending.delete('mesos')
    for (const bar of this.preset.bars) {
      if (profile.roiOverrides[bar.metric] && !profile.autoRois?.[bar.metric]?.barRect) pending.add(bar.metric)
    }
    return [...pending]
  }

  confirm(frame: ImageData, profile: CaptureProfile, at: number): {
    result: LayoutConfirmation; metrics: readonly RecognitionMetricKey[]; isCurrent: () => boolean; saveFailed: () => void
  } | null {
    const task = this.task
    if (!task?.detection) return null
    if (!this.current(task) || profile !== task.profile || frame.width !== profile.sourceWidth || frame.height !== profile.sourceHeight) {
      this.cancel(); return null
    }
    if (Date.now() > task.expires) { this.failed(task); return null }
    if (at <= task.at) return null
    const verified = this.preset.validateLayout!(frame, task.detection)
    task.confirmation.observe(verified, task.metrics, at)
    task.confirmation.verify(verified)
    task.at = at
    if (!task.metrics.some(metric => task.confirmation.confirmed[metric])) {
      if (++task.misses >= 2) this.failed(task)
      return null
    }
    this.task = null
    for (const metric of Object.keys(task.confirmation.confirmed) as RecognitionMetricKey[]) this.repairs.delete(metric)
    this.retries.delete(task.group.name)
    return { result: task.confirmation, metrics: task.metrics,
      saveFailed: () => {
        if (task.epoch !== this.epoch) return
        for (const metric of task.confirmation.replaceMetrics) this.repairs.add(metric)
        this.failed(task)
      },
      isCurrent: () => task.epoch === this.epoch && !task.controller.signal.aborted }
  }

  private current(task: Search, allowAborted = false): boolean {
    return this.task === task && task.epoch === this.epoch && (allowAborted || !task.controller.signal.aborted)
  }
  private failed(task: Search): void {
    const failures = (this.retries.get(task.group.name)?.failures ?? 0) + 1
    this.retries.set(task.group.name, { failures, after: Date.now() + retryDelays[Math.min(failures - 1, retryDelays.length - 1)]! })
    if (this.task === task) this.task = null
  }
  private report(task: Search, diagnostic: LayoutSearchDiagnostic | undefined, durationMs: number): void {
    performanceDiagnostics.observeLayout(task.group.name, { ...diagnostic, durationMs, candidates: diagnostic?.candidates ?? 0 })
  }
}
